import { useForm } from "react-hook-form"
import { useContext } from "react";
import { UserDataContext } from "./context/UserContextProvider";
import { ToastDataContext } from "./context/ToastContextProvider";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup"
import { classNames } from "./App";
import ErrorManagement, { addErrorToClass } from "./components/errormanagement";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { hrLoginEndpoint } from "./hr_endpoints";
import { useTranslation } from "react-i18next";
import CustomPasswordInput from "./components/passwordInput";

export default function HRLogin({ }) {

    const { t } = useTranslation();

    const schema = yup.object({
        email: yup.string().email(t("login.invalidEmail")).required(t("login.requiredField")),
        password: yup.string().required(t("login.requiredField"))

    })

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    })

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const userContext = useContext(UserDataContext)
    const toastContext = useContext(ToastDataContext)

    const onSubmit = async (data) => {
        const response = await fetch(hrLoginEndpoint, {
            method: 'POST',
            body: JSON.stringify({
                email: data.email,
                password: data.password
            }),
            headers: {
                "Content-Type": "application/json"
            }
        })

        if (!response.ok) {
            const textError = await response.text();
            if (textError === "email_or_password_incorrect") {
                toastContext.error(t('login.invalidCredentials'));
            } else if (textError === "email_not_confirmed") {
                toastContext.error(t('login.emailNotVerified'));
            } else {
                toastContext.error(textError);
            }
        } else {
            userContext.setUserData(await response.json());
            const redirect = searchParams.get('redirect');
            // Only allow relative paths (e.g. /hr or /hr?slotId=...) to avoid open redirect
            const safeRedirect = redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : null;
            navigate(safeRedirect || "/hr");
        }
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <div className="card w-full max-w-md shadow-2xl bg-base-100 animate__animated animate__fadeIn animate__faster">

                <div className="card-body">
                    <div className="flex flex-col items-center gap-2 mb-4">
                        <div className="placeholder">
                            <img
                                alt="TeamUp Logo"
                                src="/logo.svg"
                                className="h-40 w-auto mb-[-3rem]"
                            />
                        </div>
                        <span className="badge badge-outline badge-primary text-xs uppercase tracking-wider">
                            {t('login.roleBadge')}
                        </span>
                        <h2 className="text-2xl font-bold">{t("login.welcome")}</h2>
                        <p className="text-sm text-base-content/60">{t("login.subtitle")}</p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">{t("login.email")}</span>
                            </label>
                            <input type="email" {...register('email')} className={classNames("input input-bordered hover:input-primary focus:input-primary", addErrorToClass(errors.email))} required />
                            <ErrorManagement errorObj={errors.email} />
                        </div>

                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">{t("login.password")}</span>
                            </label>
                            <CustomPasswordInput
                                register={register('password')}
                                className={classNames("input input-bordered hover:input-primary focus:input-primary", addErrorToClass(errors.password))}
                                autoComplete="current-password"
                                showLabel={t('password.show')}
                                hideLabel={t('password.hide')}
                                required
                            />
                            <ErrorManagement errorObj={errors.password} />
                            <label className="label">
                                <span></span>
                                <Link to={"/forgot-password"}><label className="label-text-alt link link-primary hover:link-primary-focus">Ai uitat parola?</label></Link>
                            </label>
                        </div>

                        <div className="form-control mt-6">
                            <button className="btn bg-primary hover:bg-primary-dark">
                                {t("login.login")}
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                                </svg>
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )

}